import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Badge, Button, Dropdown as DropdownAntD, Menu, Result, Tabs } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { T } from 'react-translator-component';

import { IntegrationTokensTab } from './ApiTab/IntegrationTokensTab';
import { EligibilityTab } from './EligibilityTab/EligibilityTab';
import { HistoryTab } from './AuditTab/HistoryTab';
import { ConnectionsPane } from './ConnectionsTab/ConnectionsPane';
import { AcceptInviteModal } from './InvitesTab/AcceptInviteModal';
import { GeneratedTokenModal } from './InvitesTab/GeneratedTokenModal';
import { InvitePartnerModal, InviteSummary } from './InvitesTab/InvitePartnerModal';
import { InvitesTab } from './InvitesTab/InvitesTab';
import { PendingInvitesBanner } from './InvitesTab/PendingInvitesBanner';
import { ShareTokenCard } from './ShareTokenCard';
import { StationsTab } from './StationsTab/StationsTab';
import {
  Actions,
  Header,
  Page,
  PrimaryButton,
  SecondaryButton,
  Subtitle,
  Title,
  TourLink,
} from './styled';
import {
  INTEROPERABILITY_TABS,
  InteroperabilityTabKey,
} from './tabs';
import { InteroperabilityTour } from './Tour/InteroperabilityTour';
import { tabAnchorName, TourChapter } from './Tour/tourSteps';
import { hasSeenTour } from './Tour/tourStorage';
import { TransactionsTab } from './TransactionsTab/TransactionsTab';
import {
  buildSearch,
  InteroperabilityAction,
  parseUrlState,
  sameState,
} from './urlState';

import {
  currentTenantId,
  getConnections,
  getReceivedInvites,
  getSentInvites,
} from '../../../hooks/interoperability';
import {
  Connection,
  GeneratedInvite,
  InviteReview,
} from '../../../types/responses/Interoperability';
import { COLORS } from '../../../utils/theme/colors';
import InteroperabilityIcon from '../../atoms/icons/InteroperabilityIcon';
import { ListSkeleton } from '../../molecules/ListSkeleton/ListSkeleton';

/** O Dropdown do antd 4.10 nao aceita os tipos do overlay; e o mesmo cast do FinancialHeader. */
const Dropdown: any = DropdownAntD;

/**
 * Shell do módulo Interoperabilidade (MV2-1712).
 *
 * Cabeçalho, ações, banner de convites e a navegação entre as quatro abas. Ele
 * carrega uma vez as conexões e os convites, que as quatro abas consomem — cada
 * aba busca só o que é dela.
 */
export const Interoperability: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  /**
   * A URL é a fonte da verdade do estado da tela: aba, parceiro em foco e modal
   * aberto. Recarregar, voltar no navegador e mandar o link levam ao mesmo lugar.
   */
  const urlState = useMemo(() => parseUrlState(location.search), [location.search]);

  const activeTab = urlState.tab;

  /**
   * O modal de aceitar fica montado por estado local, e nao direto pela URL.
   *
   * A URL abre; o fechamento e do modal. Sem isso nao havia como limpar o
   * `action=accept` no aceite sem desmontar a tela de sucesso no mesmo instante
   * (MV2-1810).
   */
  const [aceiteAberto, setAceiteAberto] = useState(false);

  const [connections, setConnections] = useState<Connection[]>([]);
  const [receivedInvites, setReceivedInvites] = useState<InviteReview[]>([]);
  const [sentInvites, setSentInvites] = useState<InviteReview[]>([]);

  /**
   * Navega alterando só o que mudou, preservando o resto do estado da URL.
   *
   * `replace` troca a entrada atual em vez de empilhar: serve para o estado que
   * deixou de existir por efeito de uma ação, e não por navegação. Empilhar aí
   * deixaria o voltar do navegador reabrindo um fluxo já concluído (MV2-1810).
   */
  const goTo = useCallback(
    (
      change: Partial<{
        tab: InteroperabilityTabKey;
        partner: string | null;
        from: string | null;
        action: InteroperabilityAction | null;
      }>,
      { replace = false }: { replace?: boolean } = {},
    ) => {
      const next = buildSearch({ ...urlState, ...change });

      // Empilhar um estado igual ao atual faria o voltar do navegador não sair
      // da tela.
      if (sameState(location.search, next)) {
        return;
      }

      const destino = { pathname: location.pathname, search: next };

      if (replace) {
        history.replace(destino);
      } else {
        history.push(destino);
      }
    },
    [history, location.pathname, location.search, urlState],
  );

  /**
   * O aceite acabou de concluir, e fomos nos que limpamos a URL.
   *
   * E o unico caso em que o modal fica na tela sem o `action=accept`: a confirmacao do
   * aceite precisa continuar visivel. Fora dele, a URL manda — e o voltar do navegador
   * com o modal aberto tem de fechar o modal, e nao deixa-lo por cima do painel.
   */
  const aceiteConcluido = useRef(false);

  useEffect(() => {
    if (urlState.action === 'accept') {
      aceiteConcluido.current = false;
      setAceiteAberto(true);
      return;
    }

    if (!aceiteConcluido.current) {
      setAceiteAberto(false);
    }
  }, [urlState.action]);

  /**
   * De quem se espera o token, quando a pessoa chegou pelo banner ou pela aba
   * Convites. O convite sai da lista já carregada — a URL guarda só o id do tenant
   * de origem, nunca o token.
   *
   * Em `from`, e não em `partner`: `partner` é o foco de leitura de Conexões e abre
   * o painel de detalhe daquele parceiro (MV2-1823).
   */
  const expectedFrom = useMemo(
    () => receivedInvites.find((invite) => invite.tenant_source_id === urlState.from) || null,
    [receivedInvites, urlState.from],
  );

  /**
   * Convite recem-gerado, com o token em claro. Vive so aqui, em memoria, e
   * some ao fechar a tela — nao ha como recupera-lo depois.
   */
  const [generated, setGenerated] = useState<
    { invite: GeneratedInvite; summary: InviteSummary } | null
  >(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const tenantId = currentTenantId();

  /**
   * Recarrega conexoes e convites.
   *
   * `silent` recarrega sem levantar o `loading` — e o que se quer depois de uma acao,
   * quando a pagina ja tem conteudo na tela. Levantar o `loading` ali troca a pagina
   * inteira pelo esqueleto e **desmonta o que estava aberto**: foi assim que o aceite
   * derrubava a confirmacao e remontava o modal na primeira fase, fazendo o "Cole o
   * token" reaparecer sozinho (MV2-1810). O esqueleto fica para a carga inicial e para
   * o "Tentar de novo", que sao os momentos em que nao ha o que preservar.
   */
  const load = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!tenantId) {
      setLoadError(true);
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      setLoadError(false);

      const [connectionList, received, sent] = await Promise.all([
        getConnections(tenantId),
        getReceivedInvites(tenantId),
        getSentInvites(tenantId),
      ]);

      setConnections(connectionList || []);
      setReceivedInvites(received || []);
      setSentInvites(sent || []);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * O contador vem do total devolvido pela API, e não do tamanho da página
   * corrente: a aba precisa dizer quantos existem, não quantos couberam.
   */
  const counterFor = (key: InteroperabilityTabKey): number | null => {
    if (key === 'connections') return connections.length;

    // Convite pendente nos dois sentidos: o que espera resposta minha e o que
    // espera resposta do parceiro. Usado, expirado e revogado não entram — o
    // contador aponta o que exige ação, e não o tamanho da lista.
    if (key === 'invites') return receivedInvites.length + sentInvites.length;

    return null;
  };

  const [tourOpen, setTourOpen] = useState(false);

  /** Capítulo em execução. `undefined` é o tutorial completo. */
  const [tourChapter, setTourChapter] = useState<TourChapter | undefined>(undefined);

  const abrirTutorial = useCallback((chapter?: TourChapter) => {
    setTourChapter(chapter);
    setTourOpen(true);
  }, []);

  /**
   * A troca de aba que o tutorial pede.
   *
   * Memorizada porque o tutorial a tem como dependência do efeito que espera o alvo montar: uma
   * função nova a cada render reiniciaria essa espera junto com o contador do teto dela.
   *
   * Vai por `replace`: são sete passos, e empilhar histórico faria o voltar do navegador ter de
   * desandar o tutorial passo a passo para sair da tela.
   */
  const goToTourTab = useCallback(
    (tab: InteroperabilityTabKey) => goTo({ tab, partner: null, action: null }, { replace: true }),
    [goTo],
  );

  /** Se o primeiro acesso já foi avaliado nesta montagem, para não reabrir a cada recarga de dados. */
  const tourChecked = useRef(false);

  /**
   * Primeiro acesso ao módulo abre o tutorial sozinho (MV2-1791).
   *
   * Depois da carga, e não na montagem: os alvos dos passos são o conteúdo das abas, que só
   * existe quando os dados chegaram. Antes disso o balão apontaria para o esqueleto de
   * carregamento — ou para nada, se a carga falhou e a tela virou o `Result` de erro.
   */
  useEffect(() => {
    if (tourChecked.current || loading || loadError || !tenantId) {
      return;
    }

    tourChecked.current = true;

    if (!hasSeenTour()) {
      abrirTutorial();
    }
  }, [abrirTutorial, loading, loadError, tenantId]);

  return (
    <Page>
      <Header>
        {/* Alvo do primeiro passo do tutorial: o que o módulo é, antes de qualquer aba. */}
        <div data-tour="header">
          <Title>
            {/* COLORS.black como o resto do repo, e nao um hex proprio. */}
            <InteroperabilityIcon fill={COLORS.black} width={18} height={18} />
            {T('Interoperability')}
          </Title>
          <Subtitle>
            {T(
              'Each accepted invite creates a station visibility contract — bidirectional or one-way.',
            )}
          </Subtitle>
        </div>

        <Actions>
          {/*
            Entrada permanente, para quem pulou ou trocou de máquina — o "já viu" é por
            navegador (ver Tour/tourStorage.ts). Primeiro na fila porque é a ação de menor
            peso das três.

            Menu, e não dois links: o fluxo de convite ganhou capítulo próprio, e dois links de
            tutorial ao lado das duas ações do dia deixariam o cabeçalho com quatro controles
            disputando a mesma atenção.
          */}
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu>
                <Menu.Item key="full" onClick={() => abrirTutorial()}>
                  {T('See the tour again')}
                </Menu.Item>
                <Menu.Item key="invite" onClick={() => abrirTutorial('invite')}>
                  {T('Invite and token flow')}
                </Menu.Item>
              </Menu>
            }
          >
            <TourLink type="button">{T('Tutorial')}</TourLink>
          </Dropdown>
          <SecondaryButton
            data-tour="acao-token"
            type="button"
            onClick={() => goTo({ action: 'accept', from: null })}
          >
            {T('Enter token')}
          </SecondaryButton>
          <PrimaryButton
            data-tour="acao-convidar"
            type="button"
            onClick={() => goTo({ action: 'invite' })}
          >
            {T('Invite partner')}
          </PrimaryButton>
        </Actions>
      </Header>

      {/*
        Result + botao, como o financeiro faz: antes o texto dizia "tente
        novamente" e nao havia como tentar — falha de rede virava beco sem saida,
        com a pessoa tendo de recarregar a pagina inteira.
      */}
      {loadError && (
        <Result
          status="warning"
          title={T('Could not load interoperability data.')}
          extra={
            <Button type="primary" onClick={() => load()}>
              {T('Try again')}
            </Button>
          }
        />
      )}

      {/* Alvo do primeiro passo do capítulo de convite: o token que e endereco da conta. */}
      {!loadError && !loading && tenantId && (
        <div data-tour="token-da-conta">
          <ShareTokenCard tenantId={tenantId} />
        </div>
      )}

      {/*
        O banner devolve `null` sem convite pendente, então esta âncora some com ele — o passo
        do tutorial que aponta para cá é marcado como opcional e se pula.
      */}
      {!loadError && !loading && (
        <div data-tour="banner-convites">
          <PendingInvitesBanner
            received={receivedInvites}
            sentCount={sentInvites.length}
            onReview={(invite) => goTo({ action: 'accept', from: invite.tenant_source_id })}
          />
        </div>
      )}

      {/*
        Tabs do antd, como todo multi-aba do repo (FinancialList, VirtualGroupPage).
        A barra em pilha que estava aqui nao tinha precedente, nem role="tab", nem
        navegacao por seta.

        O activeKey vem da URL e o onChange navega, entao link direto e botao voltar
        continuam funcionando. O conteudo vive dentro do TabPane: o antd monta so a
        aba visitada, que e o mesmo efeito dos condicionais anteriores.
      */}
      <Tabs
        activeKey={activeTab}
        onChange={(key) =>
          goTo({ tab: key as InteroperabilityTabKey, partner: null, from: null, action: null })
        }
      >
        {INTEROPERABILITY_TABS().map((tab) => {
          const counter = counterFor(tab.key);

          return (
            <Tabs.TabPane
              key={tab.key}
              tab={
                <span>
                  {tab.label}
                  {counter !== null && (
                    <Badge
                      count={counter}
                      showZero
                      style={{ marginLeft: 8, backgroundColor: '#eef0f2', color: '#6b7280' }}
                    />
                  )}
                </span>
              }
            >
              {/*
                Âncora do tutorial (MV2-1791). Uma por aba, aqui no shell e não dentro de cada
                componente de aba: o passo aponta para a região de conteúdo, que é o que a
                pessoa precisa ver, e assim aba nova nasce com âncora sem editar nada.

                O antd mantém montada a `TabPane` de toda aba já visitada e só a esconde, então
                estes divs coexistem — o tutorial confere a caixa antes de posicionar o balão
                (ver Tour/tourFlow.ts, targetReady).
              */}
              <div data-tour={tabAnchorName(tab.key)}>
                {loading && <ListSkeleton />}

                {!loading && !loadError && tab.key === 'connections' && (
                  <ConnectionsPane
                    tenantId={tenantId as string}
                    connections={connections}
                    onChanged={() => load({ silent: true })}
                    onInvite={() => goTo({ action: 'invite' })}
                    onEnterToken={() => goTo({ action: 'accept', partner: null })}
                    expandedPartnerId={urlState.partner}
                    onExpandPartner={(partner) => goTo({ partner })}
                    map={{
                      receivedInvites,
                      sentInvites,
                      selectedPartnerId: urlState.partner,
                      onSelectPartner: (partner) => goTo({ partner }),
                      // Dentro da mesma aba: editar contrato e trocar de visao, e nao de aba.
                      onEditContract: (partner) => goTo({ partner }),
                    }}
                  />
                )}

                {!loading && !loadError && tab.key === 'invites' && (
                  <InvitesTab
                    tenantId={tenantId as string}
                    onReview={(from) => goTo({ action: 'accept', from })}
                    onChanged={() => load({ silent: true })}
                  />
                )}

                {!loading && !loadError && tab.key === 'transactions' && (
                  <TransactionsTab tenantId={tenantId as string} connections={connections} />
                )}

                {!loading && !loadError && tab.key === 'audit' && (
                  <HistoryTab tenantId={tenantId as string} connections={connections} />
                )}

                {!loading && !loadError && tab.key === 'api' && (
                  <IntegrationTokensTab tenantId={tenantId as string} />
                )}

                {!loading && !loadError && tab.key === 'stations' && (
                  <StationsTab tenantId={tenantId as string} connections={connections} />
                )}

                {!loading && !loadError && tab.key === 'eligibility' && (
                  <EligibilityTab tenantId={tenantId as string} />
                )}
              </div>
            </Tabs.TabPane>
          );
        })}
      </Tabs>

      {urlState.action === 'invite' && tenantId && (
        <InvitePartnerModal
          tenantId={tenantId}
          onClose={() => goTo({ action: null }, { replace: true })}
          onGenerated={(invite, summary) => {
            goTo({ action: null }, { replace: true });
            setGenerated({ invite, summary });
          }}
        />
      )}
      {/*
        `!loading` tambem aqui: o expectedFrom sai de receivedInvites, e montar antes da
        carga abria o modal pedindo o token de um convite que a pagina ainda ia conhecer.
        O modal sincroniza se a prop chegar depois, mas nao ha motivo para exibir a tela
        errada por um instante.
      */}
      {aceiteAberto && tenantId && !loading && (
        <AcceptInviteModal
          tenantId={tenantId}
          expectedFrom={expectedFrom}
          onClose={() => {
            aceiteConcluido.current = false;
            setAceiteAberto(false);
            // Fechar desfaz o que a propria pessoa abriu: empilhar deixava o voltar
            // do navegador reabrindo o modal de um fluxo abandonado (MV2-1810).
            goTo({ action: null, from: null }, { replace: true });
          }}
          /*
            O aceite tira o `action` da URL na hora, mas o modal continua montado
            pelo estado local para a tela de sucesso não sumir junto (MV2-1810).
            Antes o parâmetro sobrevivia ao aceite, e recarregar ou voltar reabria
            o "Cole o token" para um convite que já não existia.
          */
          onAccepted={() => {
            // Marca antes de mexer na URL: limpar o `action` e efeito do aceite, e nao
            // navegacao — o modal fica, mostrando a confirmacao (MV2-1810).
            aceiteConcluido.current = true;
            // Silenciosa de proposito: a confirmacao do aceite esta na tela, e o
            // esqueleto da recarga desmontaria o modal junto (MV2-1810).
            load({ silent: true });
            goTo({ action: null, from: null }, { replace: true });
          }}
        />
      )}
      {/*
        O tutorial fica por último: ele troca a aba ativa e precisa do shell inteiro já
        renderizado para achar as âncoras.
      */}
      <InteroperabilityTour
        open={tourOpen}
        chapter={tourChapter}
        activeTab={activeTab}
        onSelectTab={goToTourTab}
        onClose={() => setTourOpen(false)}
      />

      {generated && (
        <GeneratedTokenModal
          invite={generated.invite}
          accountCount={generated.summary.accountCount}
          stationCount={generated.summary.stationCount}
          inheritsNewSubaccounts={generated.summary.inheritsNewSubaccounts}
          ttlLabel={generated.summary.ttlLabel}
          onClose={() => {
            setGenerated(null);
            load({ silent: true });
          }}
        />
      )}
    </Page>
  );
};
